// Copyright (c) 2020 WiseTime. All rights reserved.

import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { RootState } from "app/store"
import {
  FavouriteActivityType,
  ListRecentActivityTypesResponse,
  ListFavouriteActivityTypesResponse,
  SearchActivityTypesResponse,
  SearchActivityTypesRequest,
} from "generated/legebill/site/activitytype/v4/activity_type_pb"
import { ActivityType } from "generated/legebill/type/activity_type_pb"
import * as Actions from "./actions"
import { notUndefined } from "../../lib/utils"
import { createSelector } from "@reduxjs/toolkit"

export enum RecentActivityTypesOrderBy {
  RELEVANCE = 1,
  LABEL = 2,
}

export enum ViewMode {
  SEARCH = 1,
  BROWSE = 2,
}

export type SearchActivityTypesResult = {
  searchString: string,
  response: SearchActivityTypesResponse.AsObject,
}

export type ActivityTypesCache = {
  [id: string]: ActivityType.AsObject,
}

export type ActivityTypesState = {
  recentActivityTypes: ActivityType.AsObject[],
  favouriteActivityTypes: FavouriteActivityType.AsObject[],
  // Searched activity types result
  resultActivityTypes: ActivityType.AsObject[],
  isSearching: boolean,
  searchString: string,
  searchOrderBy: SearchActivityTypesRequest.OrderBy,
  recentOrderBy: RecentActivityTypesOrderBy,
  viewMode: ViewMode,
  activityTypesCache: ActivityTypesCache,
}

export const initialState: ActivityTypesState = {
  recentActivityTypes: [],
  favouriteActivityTypes: [],
  resultActivityTypes: [],
  isSearching: false,
  searchString: "",
  searchOrderBy: SearchActivityTypesRequest.OrderBy.LABEL,
  recentOrderBy: RecentActivityTypesOrderBy.RELEVANCE,
  viewMode: ViewMode.SEARCH,
  activityTypesCache: {},
}

export const slice = createSlice({
  name: "activityTypes",
  initialState,
  reducers: {
    setRecentActivityTypes: (state, action: PayloadAction<ListRecentActivityTypesResponse.AsObject>): void => {
      state.recentActivityTypes = action.payload.recentActivityTypesList
      action.payload.recentActivityTypesList.forEach(activityType => state.activityTypesCache[activityType.id] = activityType)
    },
    setFavouriteActivityTypes: (state, action: PayloadAction<ListFavouriteActivityTypesResponse.AsObject>): void => {
      state.favouriteActivityTypes = action.payload.favouriteActivityTypesList
      action.payload.favouriteActivityTypesList
        .map(favouriteActivityType => favouriteActivityType.activityType)
        .filter(notUndefined)
        .forEach(activityType => state.activityTypesCache[activityType.id] = activityType)

    },
    setResultActivityTypes: (state, action: PayloadAction<SearchActivityTypesResult>): void => {
      if (action.payload.searchString === state.searchString) {
        state.resultActivityTypes = action.payload.response.activityTypesList
        state.isSearching = initialState.isSearching
        state.searchString = initialState.searchString
        action.payload.response.activityTypesList.forEach(activityType => state.activityTypesCache[activityType.id] = activityType)
      }
    },
    resetResultActivityTypes: (state): void => {
      state.resultActivityTypes = initialState.resultActivityTypes
      state.isSearching = initialState.isSearching
    },
    createFavouriteActivityTypeFulfilled: (state, action: PayloadAction<FavouriteActivityType.AsObject>): void => {
      state.favouriteActivityTypes.unshift(action.payload)
    },
    setSearchOrderBy: (state, action: PayloadAction<SearchActivityTypesRequest.OrderBy>): void => {
      state.searchOrderBy = action.payload
    },
    setRecentOrderBy: (state, action: PayloadAction<RecentActivityTypesOrderBy>): void => {
      state.recentOrderBy = action.payload
    },
    setViewMode: (state, action: PayloadAction<ViewMode>): void => {
      state.viewMode = action.payload
    },
    clearRecentAndFavouriteActivityTypes: (state): void => {
      state.recentActivityTypes = []
      state.favouriteActivityTypes = []
    },
    addActivityTypeToCache: (state, action: PayloadAction<ActivityType.AsObject>): void => {
      state.activityTypesCache[action.payload.id] = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(Actions.searchActivityTypes, (state, action) => {
        state.isSearching = true
        state.searchString = action.payload.getSearchText()
      })
  },
})

export const {
  setRecentActivityTypes,
  setResultActivityTypes,
  resetResultActivityTypes,
  setFavouriteActivityTypes,
  createFavouriteActivityTypeFulfilled,
  setSearchOrderBy,
  setRecentOrderBy,
  setViewMode,
  clearRecentAndFavouriteActivityTypes,
  addActivityTypeToCache,
} = slice.actions

export const selectRecentActivityTypes = createSelector(
  (state: RootState): ActivityType.AsObject[] => state.activityTypes.recentActivityTypes,
  (state: RootState): RecentActivityTypesOrderBy => state.activityTypes.recentOrderBy,
  (recentActivityTypes, recentOrderBy) => {
    if (recentOrderBy === RecentActivityTypesOrderBy.LABEL) {
      return recentActivityTypes.slice().sort((a, b) => a.label.localeCompare(b.label))
    }
    return recentActivityTypes
  }
)
export const selectResultActivityTypes = (state: RootState): ActivityType.AsObject[] => state.activityTypes.resultActivityTypes
export const selectIsSearching = (state: RootState): boolean => state.activityTypes.isSearching
export const selectSearchOrderBy = (state: RootState): SearchActivityTypesRequest.OrderBy => state.activityTypes.searchOrderBy
export const selectRecentOrderBy = (state: RootState): RecentActivityTypesOrderBy => state.activityTypes.recentOrderBy
export const selectViewMode = (state: RootState): ViewMode => state.activityTypes.viewMode
export const selectFavouriteActivityTypes = (state: RootState): FavouriteActivityType.AsObject[] => state.activityTypes.favouriteActivityTypes
export const selectActivityTypesCache = (state: RootState): ActivityTypesCache => state.activityTypes.activityTypesCache
export default slice.reducer
